/**
 * NAFos theme JavaScript — no dependencies, native Web Components.
 * Progressive enhancement: every component has a working non-JS fallback
 * or degrades gracefully.
 */

/* ---------- Utilities ---------- */

const strings = window.themeStrings || {};
const routes = window.themeRoutes || {
  cartAdd: '/cart/add.js',
  cartChange: '/cart/change.js',
  cartUpdate: '/cart/update.js',
  cart: '/cart.js',
  predictiveSearch: '/search/suggest.json',
};

/* ---------- localStorage-backed lists (wishlist, recently viewed) ---------- */

const WISHLIST_KEY = 'nafos:wishlist';
const RECENTLY_VIEWED_KEY = 'nafos:recently-viewed';
const DROP_SIGNUP_KEY = 'nafos:drop-signup';
const RECENTLY_VIEWED_MAX = 8;

function readList(key) {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeList(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* localStorage unavailable (private mode, quota) — fail silently */
  }
}

function recordRecentlyViewed(handle) {
  if (!handle) return;
  const list = readList(RECENTLY_VIEWED_KEY).filter((h) => h !== handle);
  list.unshift(handle);
  writeList(RECENTLY_VIEWED_KEY, list.slice(0, RECENTLY_VIEWED_MAX));
}

const formatMoney = (cents) => {
  const format = window.themeMoneyFormat || '${{amount}}';
  const amount = (cents / 100).toFixed(2);
  return format.replace(/\{\{\s*amount\s*\}\}/, amount);
};

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]
  ));
}

/** Render a freshly returned cart drawer section and swap its dynamic parts. */
async function refreshCartDrawer(cart, sectionHtml = '') {
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    try {
      let renderedSection = sectionHtml;
      if (!renderedSection) {
        const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const sections = await response.json();
        renderedSection = sections['cart-drawer'];
      }
      if (!renderedSection) throw new Error('Cart drawer section missing');

      const html = new DOMParser().parseFromString(renderedSection, 'text/html');
      const nextDrawer = html.querySelector('cart-drawer');
      const parts = [
        '[data-cart-drawer-body]',
        '[data-cart-drawer-count]',
        '[data-cart-drawer-subtotal]',
      ];
      for (const selector of parts) {
        const current = drawer.querySelector(selector);
        const next = html.querySelector(selector);
        if (current && next) current.replaceWith(next);
      }
      const footer = drawer.querySelector('[data-cart-drawer-footer]');
      const nextFooter = html.querySelector('[data-cart-drawer-footer]');
      if (footer && nextFooter) footer.hidden = nextFooter.hidden;
      if (nextDrawer) {
        drawer.dataset.cartItemCount = nextDrawer.dataset.cartItemCount || '0';
        drawer.dataset.cartTotalCents = nextDrawer.dataset.cartTotalCents || '0';
        if (!cart) {
          cart = {
            item_count: Number(drawer.dataset.cartItemCount),
            total_price: Number(drawer.dataset.cartTotalCents),
          };
        }
      }
    } catch (error) {
      console.error('Cart drawer refresh failed:', error);
    }
  }

  if (cart) {
    for (const bubble of document.querySelectorAll('[data-cart-count]')) {
      bubble.textContent = cart.item_count;
      bubble.classList.toggle('is-empty', cart.item_count === 0);
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }
}

async function fetchCart() {
  const response = await fetch(routes.cart);
  return response.json();
}

function setAddButtonLoading(button, loading) {
  if (!button) return;
  const label = button.querySelector('[data-add-button-label], .product-card__quick-add-label');
  if (loading) {
    if (label) {
      button.dataset.idleLabel = label.textContent.trim();
      label.textContent = strings.addingToCart || 'Adding…';
    }
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    return;
  }
  if (label && button.dataset.idleLabel) label.textContent = button.dataset.idleLabel;
  delete button.dataset.idleLabel;
  button.classList.remove('is-loading');
  button.removeAttribute('aria-busy');
}

/* ---------- Product card hover videos ---------- */
// Cards flagged with [data-hover-video] carry a muted looping <video>
// (preload="none"). On hover-capable devices it plays while hovered and
// rewinds on leave; touch devices never load it and keep the image swap.
//
// Resolution happens at the CARD level (.product-card / .tab-card), not
// at [data-hover-video] itself: product-card has a full-card overlay
// link (`.product-card__link::after`, for a single accessible tab stop)
// sitting above the media in paint order, so real pointer events target
// that overlay — closest('[data-hover-video]') from there finds nothing
// because the overlay is a sibling, not a descendant, of the media
// element. Walking up to the shared card ancestor first avoids that.
if (window.matchMedia('(hover: hover)').matches) {
  const cardVideoFrom = (event) => {
    const card = event.target.closest?.('.product-card, .tab-card');
    if (!card || card.contains(event.relatedTarget)) return null;
    return card.querySelector('[data-hover-video] video');
  };

  document.addEventListener('pointerover', (event) => {
    const video = cardVideoFrom(event);
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    video.play().catch(() => {});
  });

  document.addEventListener('pointerout', (event) => {
    const video = cardVideoFrom(event);
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  });
}

/* ---------- Product card touch previews ---------- */

(function initMobileProductCardPreviews() {
  const mobileCards = window.matchMedia('(max-width: 749px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const resetVideo = (card) => {
    const video = card.querySelector('.product-card__video');
    card.classList.remove('is-mobile-video-playing');
    if (!video) return;
    video.pause();
    try {
      video.currentTime = 0;
    } catch {
      /* The video may not have loaded metadata yet. */
    }
  };

  const closePreview = (card) => {
    card.classList.remove('is-mobile-preview');
    resetVideo(card);
  };

  const closeOtherPreviews = (currentCard = null) => {
    for (const card of document.querySelectorAll('.product-card.is-mobile-preview')) {
      if (card !== currentCard) closePreview(card);
    }
  };

  const openPreview = (card) => {
    closeOtherPreviews(card);
    card.classList.add('is-mobile-preview');

    const video = card.querySelector('.product-card__video');
    if (!video || reduceMotion.matches) return;

    video.muted = true;
    video.playsInline = true;
    video.play()
      .then(() => {
        if (card.classList.contains('is-mobile-preview')) {
          card.classList.add('is-mobile-video-playing');
        } else {
          resetVideo(card);
        }
      })
      .catch(() => {
        // Keep the second image visible when autoplay is unavailable.
      });
  };

  const tapLandedOnMedia = (event, card) => {
    if (event.target.closest?.('.product-card__media')) return true;

    // The title link stretches over the whole card. When its pseudo-element
    // receives the tap, use the pointer coordinates to determine whether the
    // shopper actually touched the media rather than the title below it.
    const media = card.querySelector('.product-card__media');
    if (!media) return false;
    const rect = media.getBoundingClientRect();
    return event.clientX >= rect.left
      && event.clientX <= rect.right
      && event.clientY >= rect.top
      && event.clientY <= rect.bottom;
  };

  document.addEventListener('click', (event) => {
    if (!mobileCards.matches || event.detail === 0) return;

    const card = event.target.closest?.('.product-card');
    if (!card || !tapLandedOnMedia(event, card)) {
      closeOtherPreviews();
      return;
    }

    // The first tap is a quiet preview: reveal the second medium and actions.
    // Once open, the same link keeps its native behaviour on the second tap.
    if (!card.classList.contains('is-mobile-preview')) {
      event.preventDefault();
      if (document.activeElement instanceof HTMLElement && card.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      openPreview(card);
    }
  });

  mobileCards.addEventListener('change', (event) => {
    if (!event.matches) closeOtherPreviews();
  });
})();

/* ---------- Reveal on scroll ---------- */

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      }
    }
  },
  { rootMargin: '0px 0px -8% 0px' }
);

function observeReveals(root = document) {
  if (document.documentElement.classList.contains('no-reveal')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    for (const el of root.querySelectorAll('.reveal')) el.classList.add('is-revealed');
    return;
  }
  for (const el of root.querySelectorAll('.reveal:not(.is-revealed)')) {
    revealObserver.observe(el);
  }
}

document.addEventListener('DOMContentLoaded', () => observeReveals());
document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));

/* ---------- Sticky header: condense to the minimal bar once pinned ---------- */

(function initStickyHeaderMinimal() {
  const header = document.querySelector('.header--sticky');
  if (!header) return;

  // The header pins to the top once you've scrolled past whatever sits above
  // it (the announcement bar). We toggle the compact state off a FIXED scroll
  // distance — never off the header's own getBoundingClientRect(), because
  // that value moves as the header resizes AND bounces during iOS rubber-band
  // overscroll at the very top; reading it fed the resize back into the
  // trigger, flip-flopping is-stuck and causing the stutter/freeze when the
  // full header re-opened at the top. A hysteresis gap (condense a little
  // below the pin point, expand a little above it) keeps a momentum bounce or
  // a resize from oscillating the state across the boundary.
  const GAP = 16;
  let pin = 0;
  let stuck = false;
  let ticking = false;

  const measure = () => {
    // Distance from document top to the header = height of the announcement
    // bar above it. It isn't sticky, so its height is stable whatever the
    // scroll position — a reliable pin point that doesn't depend on state.
    const bar = document.querySelector('.announcement-bar');
    pin = bar ? bar.getBoundingClientRect().height : 0;
  };

  const update = () => {
    ticking = false;
    const y = window.scrollY;
    if (!stuck && y > pin + GAP) {
      stuck = true;
      header.classList.add('is-stuck');
    } else if (stuck && y < Math.max(0, pin - GAP)) {
      stuck = false;
      header.classList.remove('is-stuck');
    }
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      measure();
      update();
    },
    { passive: true }
  );

  measure();
  update();
})();

/* ---------- <quantity-input> ---------- */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-action]');
      if (!button || !this.input) return;
      event.preventDefault();
      const step = button.dataset.action === 'increase' ? 1 : -1;
      const min = Number(this.input.min || 0);
      const next = Math.max(min, Number(this.input.value || 0) + step);
      if (next !== Number(this.input.value)) {
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- <cart-drawer> ---------- */

class CartDrawer extends HTMLElement {
  #trigger = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.liveRegion = this.querySelector('[data-cart-live-region]');

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) this.close();
      if (event.target.closest('[data-remove-item]')) {
        const line = event.target.closest('[data-remove-item]').dataset.lineIndex;
        this.#changeLine(line, 0);
      }
      // Click on backdrop (the dialog itself, outside the inner panel)
      if (event.target === this.dialog) this.close();
    });

    this.addEventListener('change', (event) => {
      const input = event.target.closest('.quantity__input');
      if (input && input.dataset.index) {
        this.#changeLine(input.dataset.index, Number(input.value));
      }
    });

    document.addEventListener('cart:open-drawer', () => this.open());
  }

  open(trigger) {
    if (!this.dialog || this.dialog.open) return;
    this.#trigger = trigger || document.activeElement;
    this.dialog.showModal();
  }

  setUpdating(updating) {
    this.classList.toggle('is-updating', updating);
    this.querySelector('[data-cart-drawer-body]')?.setAttribute('aria-busy', String(updating));
  }

  close() {
    if (!this.dialog?.open) return;
    this.dialog.close();
    this.#trigger?.focus?.();
  }

  announce(message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    requestAnimationFrame(() => {
      this.liveRegion.textContent = message;
    });
  }

  async #changeLine(line, quantity) {
    this.setUpdating(true);
    try {
      const response = await fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity,
          sections: ['cart-drawer'],
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      // On a rejected change (e.g. a one-of-a-kind item already maxed out,
      // 422) change.js still returns JSON; re-sync the drawer from the true
      // cart so the quantity field snaps back instead of keeping the value
      // the shopper typed, and surface the reason.
      const cart = response.ok ? data : await fetchCart();
      await refreshCartDrawer(cart, response.ok ? data.sections?.['cart-drawer'] : '');
      this.announce(response.ok ? (strings.cartUpdated || 'Cart updated') : (data.description || strings.cartError || 'Could not update cart'));
    } catch (error) {
      console.error('Cart change failed:', error);
      try { await refreshCartDrawer(await fetchCart()); } catch {}
      this.announce(strings.cartError || 'Could not update cart');
    } finally {
      this.setUpdating(false);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- <cart-page> — live AJAX line updates on the full cart page ---------- */

class CartPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;

    // Quantity changes (typed value or the +/- buttons, which dispatch change).
    this.addEventListener('change', (event) => {
      const input = event.target.closest('.quantity__input');
      if (input && input.dataset.index) {
        this.#changeLine(input.dataset.index, Number(input.value));
      }
    });

    // "Remove" links keep their href as a no-JS fallback; intercept with AJAX.
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      this.#changeLine(remove.dataset.index, 0);
    });
  }

  async #changeLine(line, quantity) {
    this.classList.add('is-updating');
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity }),
      });
      // Re-render either way: on a rejected change (e.g. a one-of-a-kind item
      // that can't exceed 1, 422) the fresh section reflects the true cart, so
      // the quantity field snaps back instead of keeping the shopper's value.
      const cart = response.ok ? await response.json() : await fetchCart();
      await this.#rerender();
      // Keep the drawer + header count badges in sync with the same change.
      await refreshCartDrawer(cart);
      this.#announce(response.ok ? (strings.cartUpdated || 'Cart updated') : (strings.cartError || 'Could not update cart'));
    } catch (error) {
      console.error('Cart update failed:', error);
      await this.#rerender();
      this.#announce(strings.cartError || 'Could not update cart');
    } finally {
      this.classList.remove('is-updating');
      this.setAttribute('aria-busy', 'false');
    }
  }

  #announce(message) {
    const region = this.querySelector('[data-cart-page-live-region]');
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  // Re-render just this section via the Section Rendering API and swap its
  // contents, so line totals, the subtotal and empty-state all refresh live.
  async #rerender() {
    const response = await fetch(`${routes.cartPage}?section_id=${encodeURIComponent(this.sectionId)}`);
    if (!response.ok) return;
    const text = await response.text();
    const next = new DOMParser().parseFromString(text, 'text/html').querySelector('cart-page');
    if (next) this.innerHTML = next.innerHTML;
  }
}
customElements.define('cart-page', CartPage);

/* ---------- <drop-countdown> — live ticking timer to a drop release ---------- */

class DropCountdown extends HTMLElement {
  connectedCallback() {
    this.releaseMs = Number(this.dataset.release || 0) * 1000;
    this.departureSent = false;
    this.slots = {
      days: this.querySelector('[data-days]'),
      hours: this.querySelector('[data-hours]'),
      mins: this.querySelector('[data-mins]'),
      secs: this.querySelector('[data-secs]'),
    };
    if (!this.releaseMs) return;
    this.#scheduleDeparture();
    this.#tick();
    this.timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    clearTimeout(this.departureTimer);
  }

  #scheduleDeparture() {
    const departureDelay = this.releaseMs - Date.now() - 3000;
    if (departureDelay <= 0) {
      if (this.releaseMs > Date.now()) this.#announceDeparture();
      return;
    }

    // Browsers cap a single timeout at roughly 24.8 days. Re-checking in
    // bounded steps also keeps a far-future drop's departure time precise.
    const maxTimeout = 2147480000;
    this.departureTimer = setTimeout(
      () => this.#scheduleDeparture(),
      Math.min(departureDelay, maxTimeout)
    );
  }

  #announceDeparture() {
    if (this.departureSent || this.classList.contains('is-live')) return;
    this.departureSent = true;
    this.classList.add('is-departing');
    this.dispatchEvent(new CustomEvent('drop:departing', { bubbles: true }));
  }

  #tick() {
    const diff = this.releaseMs - Date.now();
    if (diff <= 0) {
      clearInterval(this.timer);
      clearTimeout(this.departureTimer);
      this.#paint(0, 0, 0, 0);
      this.classList.add('is-live');
      // Let an enclosing <drop-gate> reveal the real buy button.
      this.dispatchEvent(new CustomEvent('drop:live', { bubbles: true }));
      return;
    }
    const total = Math.floor(diff / 1000);
    this.#paint(
      Math.floor(total / 86400),
      Math.floor((total % 86400) / 3600),
      Math.floor((total % 3600) / 60),
      total % 60
    );
  }

  #paint(d, h, m, s) {
    const pad = (n) => String(n).padStart(2, '0');
    if (this.slots.days) this.slots.days.textContent = String(d);
    if (this.slots.hours) this.slots.hours.textContent = pad(h);
    if (this.slots.mins) this.slots.mins.textContent = pad(m);
    if (this.slots.secs) this.slots.secs.textContent = pad(s);
  }
}
customElements.define('drop-countdown', DropCountdown);

/* ---------- <drop-sound> — opt-in, once-per-release drop chime ---------- */

class DropSound extends HTMLElement {
  connectedCallback() {
    this.releaseMs = Number(this.dataset.release || 0) * 1000;
    this.toggleButton = this.querySelector('[data-drop-sound-toggle]');
    this.label = this.querySelector('[data-drop-sound-label]');
    this.statusRegion = this.querySelector('[data-drop-sound-status]');
    this.fallbackAudio = this.querySelector('[data-drop-sound-fallback]');
    if (!this.releaseMs || !this.toggleButton || !this.dataset.src) return;

    this.preferenceKey = 'nafos:drop-bell-enabled';
    this.playedKey = `nafos:drop-bell-played:${Math.floor(this.releaseMs / 1000)}`;
    this.enabled = this.#readSession(this.preferenceKey) === 'true';
    this.played = this.#readSession(this.playedKey) === 'true';
    this.onToggle = () => this.#toggle();
    this.onDropLive = () => this.#handleRelease();
    this.onFallbackEnded = () => this.#complete();

    this.toggleButton.addEventListener('click', this.onToggle);
    document.addEventListener('drop:live', this.onDropLive);
    this.fallbackAudio?.addEventListener('ended', this.onFallbackEnded);
    this.leadMs = Number(this.dataset.leadSeconds || 3600) * 1000;
    this.#paint();

    if (this.enabled) this.#armPrimeGesture();
    if (this.played) {
      this.hidden = true;
      return;
    }
    this.#scheduleReveal();
    this.#scheduleRelease();
  }

  disconnectedCallback() {
    clearTimeout(this.releaseTimer);
    clearTimeout(this.revealTimer);
    clearTimeout(this.feedbackTimer);
    clearTimeout(this.fallbackPreviewTimer);
    this.toggleButton?.removeEventListener('click', this.onToggle);
    document.removeEventListener('drop:live', this.onDropLive);
    this.fallbackAudio?.removeEventListener('ended', this.onFallbackEnded);
    this.#removePrimeGesture();
    this.#stopSource();
  }

  // The bell is only offered near the release — a drop still days out doesn't
  // need a sound toggle trailing the visitor around the shop. Someone already
  // waiting on the page sees it appear on its own when the window opens.
  #scheduleReveal() {
    const untilWindow = this.releaseMs - this.leadMs - Date.now();
    if (untilWindow <= 0) {
      this.hidden = false;
      return;
    }
    this.hidden = true;
    // setTimeout tops out around 24.8 days and fires immediately past that,
    // which would reveal the bell far too early for a distant drop.
    if (untilWindow > 2147483647) return;
    this.revealTimer = setTimeout(() => {
      this.hidden = false;
    }, untilWindow);
  }

  #scheduleRelease() {
    const delay = this.releaseMs - Date.now();
    if (delay <= 0) {
      this.#handleRelease();
      return;
    }

    const maxTimeout = 2147480000;
    this.releaseTimer = setTimeout(
      () => this.#scheduleRelease(),
      Math.min(delay, maxTimeout)
    );
  }

  async #toggle() {
    if (this.loading) return;
    if (this.enabled) {
      await this.#disable();
      return;
    }

    this.loading = true;
    this.classList.add('is-loading');
    this.toggleButton.setAttribute('aria-busy', 'true');
    const ready = await this.#prepareAudio();
    this.loading = false;
    this.classList.remove('is-loading');
    this.toggleButton.removeAttribute('aria-busy');

    if (!ready) {
      this.#announce(this.dataset.blockedMessage);
      return;
    }

    this.enabled = true;
    this.#writeSession(this.preferenceKey, 'true');
    this.#paint();
    this.#showFeedback();
    this.#announce(this.dataset.enabledMessage);
    this.#playPreview();
  }

  async #disable() {
    this.enabled = false;
    this.#writeSession(this.preferenceKey, 'false');
    this.#removePrimeGesture();
    this.#stopSource();
    this.fallbackAudio?.pause();
    try {
      await this.audioContext?.suspend();
    } catch (error) {
      // The context may already have been closed by the browser.
    }
    this.#paint();
    this.#showFeedback();
    this.#announce(this.dataset.disabledMessage);
  }

  async #prepareAudio() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return this.#prepareFallback();

    try {
      if (!this.audioContext) this.audioContext = new AudioContextClass();
      if (this.audioContext.state !== 'running') await this.audioContext.resume();
      if (this.audioContext.state !== 'running') return false;

      if (!this.bufferPromise) this.bufferPromise = this.#loadAudioBuffer();
      this.audioBuffer = await this.bufferPromise;
      return Boolean(this.audioBuffer);
    } catch (error) {
      this.bufferPromise = null;
      return false;
    }
  }

  async #loadAudioBuffer() {
    const response = await fetch(this.dataset.src, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const audioData = await response.arrayBuffer();
    return this.audioContext.decodeAudioData(audioData);
  }

  async #prepareFallback() {
    if (!this.fallbackAudio) return false;
    try {
      await this.fallbackAudio.play();
      this.fallbackAudio.pause();
      this.fallbackAudio.currentTime = 0;
      return true;
    } catch (error) {
      return false;
    }
  }

  #playPreview() {
    if (this.audioContext && this.audioBuffer) {
      this.#startBuffer(true);
      return;
    }
    if (!this.fallbackAudio) return;

    this.fallbackAudio.volume = 0.18;
    this.fallbackAudio.currentTime = 0;
    this.fallbackAudio.play().catch(() => {});
    clearTimeout(this.fallbackPreviewTimer);
    this.fallbackPreviewTimer = setTimeout(() => {
      this.fallbackAudio.pause();
      this.fallbackAudio.currentTime = 0;
    }, 650);
  }

  #startBuffer(preview) {
    this.#stopSource();
    const source = this.audioContext.createBufferSource();
    const gain = this.audioContext.createGain();
    source.buffer = this.audioBuffer;
    gain.gain.setValueAtTime(preview ? 0.18 : 0.55, this.audioContext.currentTime);
    source.connect(gain);
    gain.connect(this.audioContext.destination);
    source.start();

    if (preview) {
      this.previewSource = source;
      source.onended = () => {
        if (this.previewSource === source) this.previewSource = null;
      };
      source.stop(this.audioContext.currentTime + Math.min(0.65, this.audioBuffer.duration));
      return;
    }

    this.activeSource = source;
    source.onended = () => {
      if (this.activeSource !== source) return;
      this.activeSource = null;
      this.#complete();
    };
  }

  #stopSource() {
    for (const source of [this.previewSource, this.activeSource]) {
      if (!source) continue;
      source.onended = null;
      try {
        source.stop();
      } catch (error) {
        // A source that has already ended cannot be stopped twice.
      }
    }
    this.previewSource = null;
    this.activeSource = null;
  }

  async #handleRelease() {
    if (this.playing || this.played) return;
    const lateness = Date.now() - this.releaseMs;
    if (lateness < -750) return;

    clearTimeout(this.releaseTimer);
    if (!this.enabled || document.visibilityState !== 'visible' || lateness > 5000) {
      this.#complete();
      return;
    }

    this.playing = true;
    const ready = await this.#prepareAudio();
    if (!ready) {
      this.playing = false;
      this.#announce(this.dataset.blockedMessage);
      this.#complete();
      return;
    }

    this.played = true;
    this.#writeSession(this.playedKey, 'true');
    this.classList.add('is-ringing');
    this.#announce(this.dataset.liveMessage);

    if (this.audioContext && this.audioBuffer) {
      this.#startBuffer(false);
      return;
    }

    this.fallbackAudio.volume = 0.55;
    this.fallbackAudio.currentTime = 0;
    try {
      await this.fallbackAudio.play();
    } catch (error) {
      this.#announce(this.dataset.blockedMessage);
      this.#complete();
    }
  }

  #armPrimeGesture() {
    if (this.onPrimeGesture) return;
    this.onPrimeGesture = () => {
      this.#removePrimeGesture();
      this.#prepareAudio();
    };
    document.addEventListener('pointerdown', this.onPrimeGesture, { capture: true, once: true });
    document.addEventListener('keydown', this.onPrimeGesture, { capture: true, once: true });
  }

  #removePrimeGesture() {
    if (!this.onPrimeGesture) return;
    document.removeEventListener('pointerdown', this.onPrimeGesture, true);
    document.removeEventListener('keydown', this.onPrimeGesture, true);
    this.onPrimeGesture = null;
  }

  #paint() {
    this.classList.toggle('is-enabled', this.enabled);
    this.toggleButton.setAttribute('aria-pressed', String(this.enabled));
    this.toggleButton.setAttribute(
      'aria-label',
      this.enabled ? this.dataset.disableLabel : this.dataset.enableLabel
    );
    if (this.label) this.label.textContent = this.enabled ? this.dataset.onLabel : this.dataset.offLabel;
  }

  #showFeedback() {
    clearTimeout(this.feedbackTimer);
    this.classList.add('is-feedback');
    this.feedbackTimer = setTimeout(() => this.classList.remove('is-feedback'), 1800);
  }

  #announce(message) {
    if (!this.statusRegion || !message) return;
    this.statusRegion.textContent = '';
    requestAnimationFrame(() => {
      this.statusRegion.textContent = message;
    });
  }

  #complete() {
    if (this.classList.contains('is-complete')) return;
    clearTimeout(this.releaseTimer);
    this.classList.remove('is-ringing');
    this.classList.add('is-complete');
    setTimeout(() => {
      this.hidden = true;
    }, 550);
  }

  #readSession(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  #writeSession(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      // Private browsing or storage policies can disable session storage.
    }
  }
}
customElements.define('drop-sound', DropSound);

/* ---------- <drop-gate> — reveals the real buy button when the drop goes live ----------
   Before release the server renders a disabled "Drops soon" state inside a
   [data-buy-area] wrapper. When the enclosed countdown fires `drop:live`, we
   re-fetch this product section from the server and swap the buy area in.
   Because the swap uses the SERVER's freshly rendered markup, the live button
   only appears once the server's own clock has passed the release — a visitor
   can't unlock it early by moving their device clock forward. */

class DropGate extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.onDropDeparting = () => this.classList.add('is-butterfly-departing');
    this.onDropLive = () => this.#activate();
    this.addEventListener('drop:departing', this.onDropDeparting);
    this.addEventListener('drop:live', this.onDropLive);

    // Countdown elements are registered first. Recover their state when this
    // gate is upgraded after an event has already fired during page startup.
    const countdown = this.querySelector('drop-countdown');
    if (countdown?.classList.contains('is-departing')) this.onDropDeparting();
    if (countdown?.classList.contains('is-live')) this.onDropLive();
  }

  disconnectedCallback() {
    this.removeEventListener('drop:departing', this.onDropDeparting);
    this.removeEventListener('drop:live', this.onDropLive);
  }

  async #activate() {
    if (this.dataset.activating) return;
    this.dataset.activating = 'true';
    // A small grace period so the server clock is unambiguously past release.
    await new Promise((resolve) => setTimeout(resolve, 1000));
    try {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const nextBuy = doc.querySelector('[data-buy-area]');
      const currentBuy = this.closest('[data-buy-area]');
      if (nextBuy && currentBuy) {
        currentBuy.replaceWith(nextBuy);
      } else {
        window.location.reload();
      }
    } catch (error) {
      // If anything goes wrong, a reload still reveals the now-live button.
      window.location.reload();
    }
  }
}
customElements.define('drop-gate', DropGate);

/* ---------- <drop-signup> — the drop marquee's email-list dialog ---------- */

class DropSignup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.openButton = this.querySelector('[data-drop-open]');
    if (!this.dialog) return;

    this.openButton?.addEventListener('click', () => this.dialog.showModal());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-drop-close]')) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => this.openButton?.focus());

    this.#restoreAfterSubmit();
  }

  /* Shopify handles {% form 'customer' %} with a full page reload, which would
     close the dialog and hide the confirmation the visitor just earned. We flag
     the submit before the reload and re-open on the way back. The flag is what
     keeps this specific to us: form.posted_successfully? is true page-wide, so
     without it the homepage newsletter signup would pop this dialog open too. */
  #restoreAfterSubmit() {
    const form = this.querySelector('form');
    form?.addEventListener('submit', () => {
      try {
        sessionStorage.setItem(DROP_SIGNUP_KEY, '1');
      } catch (error) {
        // Private browsing — the dialog just won't re-open. Not worth failing over.
      }
    });

    let submitted = false;
    try {
      submitted = sessionStorage.getItem(DROP_SIGNUP_KEY) === '1';
      sessionStorage.removeItem(DROP_SIGNUP_KEY);
    } catch (error) {
      return;
    }
    // Re-open for the error too, so a rejected address is seen and fixable.
    if (submitted && this.querySelector('.newsletter-form__success, .field__error')) {
      this.dialog.showModal();
    }
  }
}
customElements.define('drop-signup', DropSignup);

/** Add a variant to the cart and open the drawer (shared by quick-add + product form). */
async function addToCart(body, triggerButton) {
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    drawer.open(triggerButton);
    drawer.setUpdating(true);
  }
  const requestBody = {
    ...body,
    sections: ['cart-drawer'],
    sections_url: window.location.pathname,
  };
  try {
    const response = await fetch(routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(requestBody),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'Add to cart failed');

    await refreshCartDrawer(null, data.sections?.['cart-drawer']);

    if (drawer) {
      drawer.announce(strings.itemAdded || 'Item added to cart');
    } else {
      window.location.href = window.themeRoutes?.cartPage || '/cart';
    }
    return data;
  } catch (error) {
    drawer?.announce(error.message || strings.cartError || 'Could not update cart');
    throw error;
  } finally {
    drawer?.setUpdating(false);
  }
}

/* ---------- <quick-add> ---------- */

class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-variant-id]');
    this.button?.addEventListener('click', async (event) => {
      event.preventDefault();
      this.button.disabled = true;
      setAddButtonLoading(this.button, true);
      try {
        await addToCart({ items: [{ id: Number(this.button.dataset.variantId), quantity: 1 }] }, this.button);
      } catch (error) {
        console.error(error);
      } finally {
        this.button.disabled = false;
        setAddButtonLoading(this.button, false);
      }
    });
  }
}
customElements.define('quick-add', QuickAdd);

/* ---------- <product-form> ---------- */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.errorEl = this.querySelector('[data-form-error]');
    this.form?.addEventListener('submit', this.#handleSubmit.bind(this));
  }

  async #handleSubmit(event) {
    event.preventDefault();
    // Query the button live rather than trusting a cached reference: the buy
    // area is re-rendered when a drop unlocks (and when variants change), so
    // the element captured at connect time may be stale/detached by now.
    const submitButton = this.querySelector('[type="submit"]');
    const errorEl = this.querySelector('[data-form-error]');
    if (!submitButton || submitButton.getAttribute('aria-disabled') === 'true') return;
    submitButton.setAttribute('aria-disabled', 'true');
    setAddButtonLoading(submitButton, true);
    if (errorEl) errorEl.hidden = true;

    try {
      const formData = new FormData(this.form);
      const items = [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }];
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value) properties[match[1]] = value;
      }
      if (Object.keys(properties).length) items[0].properties = properties;
      await addToCart({ items }, submitButton);
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
    } finally {
      submitButton.removeAttribute('aria-disabled');
      setAddButtonLoading(submitButton, false);
    }
  }
}
customElements.define('product-form', ProductForm);

/* ---------- <variant-picker> ---------- */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-variants-json]');
    if (!dataEl) return;
    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }
    this.addEventListener('change', () => this.#onChange());
  }

  get selectedOptions() {
    const options = [];
    for (const fieldset of this.querySelectorAll('[data-option-index]')) {
      const checked = fieldset.querySelector('input:checked');
      const select = fieldset.querySelector('select');
      options[Number(fieldset.dataset.optionIndex)] = checked ? checked.value : select?.value;
    }
    return options;
  }

  #onChange() {
    const selected = this.selectedOptions;
    const variant = this.variants.find((v) =>
      v.options.every((option, index) => option === selected[index])
    );
    this.dispatchEvent(
      new CustomEvent('variant:change', { detail: { variant }, bubbles: true })
    );
    if (!variant) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }
}
customElements.define('variant-picker', VariantPicker);

/* ---------- <main-product> — wires picker to price/button/media ---------- */

class MainProduct extends HTMLElement {
  connectedCallback() {
    this.addEventListener('variant:change', (event) => this.#update(event.detail.variant));
    // Archived pieces are display-only; keep them out of the recently-viewed trail.
    if (!this.dataset.archived) recordRecentlyViewed(this.dataset.productHandle);
    this.#writeDeliveryEstimate();
  }

  // Arrival dates are computed here rather than in Liquid: the storefront
  // caches rendered pages, so a server-side "today" can be days out of date.
  #writeDeliveryEstimate() {
    const el = this.querySelector('[data-delivery-estimate]');
    if (!el) return;
    const min = Number(el.dataset.minDays);
    const max = Number(el.dataset.maxDays);
    if (!min || !max || !el.dataset.template) return;
    const format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { month: 'short', day: 'numeric' });
    const day = (offset) => format.format(new Date(Date.now() + offset * 86400000));
    el.textContent = ' ' + el.dataset.template.replace('[[from]]', day(min)).replace('[[to]]', day(max));
  }

  #update(variant) {
    const idInput = this.querySelector('input[name="id"]');
    const button = this.querySelector('[data-add-button]');
    const buttonLabel = this.querySelector('[data-add-button-label]');
    const priceEl = this.querySelector('[data-price]');

    if (!variant) {
      if (button) button.setAttribute('aria-disabled', 'true');
      if (buttonLabel) buttonLabel.textContent = strings.unavailable || 'Unavailable';
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (priceEl) {
      // Rebuild the same markup snippets/price.liquid renders — the .price
      // wrapper carries the heading font/size, so swapping in bare spans
      // would visibly shrink the price after a variant change.
      priceEl.innerHTML = variant.compare_at_price > variant.price
        ? `<div class="price price--sale product__price"><s class="price__compare">${formatMoney(variant.compare_at_price)}</s> <span class="price__current">${formatMoney(variant.price)}</span></div>`
        : `<div class="price product__price"><span class="price__current">${formatMoney(variant.price)}</span></div>`;
    }
    // Purchasable with tracked inventory at zero = "continue selling when out
    // of stock" → made-to-order (mirrors the server-rendered state). A variant
    // named "preorder …" is made-to-order whatever its stock count says.
    const preorder = variant.available && (
      (variant.inventory_managed && variant.inventory_quantity <= 0) || variant.preorder_named === true
    );

    const scarcityEl = this.querySelector('[data-scarcity]');
    if (scarcityEl) {
      let scarcityHtml = '';
      if (!preorder && variant.available && variant.inventory_managed && variant.inventory_quantity >= 1 && variant.inventory_quantity <= 3) {
        const label = variant.inventory_quantity === 1
          ? (strings.onlyOneLeft || 'Only one exists — once it finds a home, it’s gone')
          : (strings.lowStock || 'Only [[count]] left').replace('[[count]]', variant.inventory_quantity);
        scarcityHtml = `<p class="product__scarcity"><span class="product__scarcity-dot" aria-hidden="true"></span>${label}</p>`;
      }
      scarcityEl.innerHTML = scarcityHtml;
    }

    const deliveryEl = this.querySelector('[data-delivery]');
    if (deliveryEl) {
      // A sold-out variant ships nothing, so neither promise is shown for it.
      deliveryEl.querySelector('[data-delivery-ready]')?.toggleAttribute('hidden', preorder || !variant.available);
      deliveryEl.querySelector('[data-delivery-preorder]')?.toggleAttribute('hidden', !preorder);
    }

    if (button && buttonLabel) {
      button.classList.toggle('product__add-button--preorder', preorder);
      if (preorder) button.dataset.preorder = 'true';
      else delete button.dataset.preorder;
      if (variant.available) {
        button.removeAttribute('aria-disabled');
        buttonLabel.textContent = preorder
          ? (strings.preorder || 'Pre-order')
          : (strings.addToCart || 'Add to cart');
      } else {
        button.setAttribute('aria-disabled', 'true');
        buttonLabel.textContent = strings.soldOut || 'Sold out';
      }
    }

    if (variant.featured_media?.id) {
      // Scope to the carousel track: [data-media-id] also exists on the
      // thumbnail links, and matching those first left the gallery parked.
      const track = this.querySelector('[data-carousel-track]');
      const slide = track?.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (track && slide) {
        const delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
        if (Math.abs(delta) > 1) track.scrollBy({ left: delta, behavior: 'smooth' });
      }
      const thumb = this.querySelector(`.product__thumb[data-media-id="${variant.featured_media.id}"]`);
      const thumbScroller = this.querySelector('[data-thumbs-scroller]');
      if (thumb && thumbScroller) {
        const thumbRect = thumb.getBoundingClientRect();
        const scrollerRect = thumbScroller.getBoundingClientRect();
        const outside =
          thumbRect.left < scrollerRect.left || thumbRect.right > scrollerRect.right ||
          thumbRect.top < scrollerRect.top || thumbRect.bottom > scrollerRect.bottom;
        if (outside) {
          thumbScroller.scrollBy({
            left: thumbRect.left - scrollerRect.left,
            top: thumbRect.top - scrollerRect.top,
            behavior: 'smooth',
          });
        }
      }
    }
  }
}
customElements.define('main-product', MainProduct);

/* ---------- <infinite-collection> — endless loading for collection grids ----------
   Progressive enhancement over server pagination: a sentinel below the grid
   fetches paginate.next.url and appends the next page's cards. The numbered
   pagination stays in the DOM underneath (hidden by CSS while JS is running),
   so it still works without JS and crawlers keep a real link to every page. */

class InfiniteCollection extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-infinite-grid]');
    this.sentinel = this.querySelector('[data-infinite-sentinel]');
    this.loading = false;
    this.loadingAll = false;
    this.controller = new AbortController();
    this.#observeSentinel();
  }

  #observeSentinel() {
    this.observer?.disconnect();
    if (!this.grid || !this.sentinel) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#loadNext();
      },
      { rootMargin: '900px 0px' }
    );
    this.observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
  }

  get hasMore() {
    return Boolean(this.sentinel?.dataset.nextUrl);
  }

  // Replace the server fallback with an availability-filtered Section
  // Rendering response while retaining this custom element and its listeners.
  // Keeping the same grid node also preserves the collection's type-filter
  // references; only its cards and pagination state change.
  replacePage(source) {
    const nextGrid = source?.querySelector('[data-infinite-grid]');
    if (!this.grid || !nextGrid) return false;

    this.controller?.abort();
    this.controller = new AbortController();
    this.observer?.disconnect();
    this.grid.replaceChildren(...nextGrid.children);

    const nextSentinel = source.querySelector('[data-infinite-sentinel]');
    this.sentinel?.remove();
    this.sentinel = null;
    if (nextSentinel) {
      const emptyNote = this.querySelector('[data-type-empty]');
      this.insertBefore(nextSentinel, emptyNote || null);
      this.sentinel = nextSentinel;
    }

    this.loading = false;
    this.loadingAll = false;
    observeReveals(this.grid);
    this.#observeSentinel();
    this.dispatchEvent(new CustomEvent('collection:appended', { bubbles: true }));
    return true;
  }

  // Pull every remaining page in sequence. The type filter needs the whole
  // collection in the DOM before it can hide anything, otherwise a choice
  // would only apply to the cards scrolled past so far.
  async loadAll() {
    if (this.loadingAll) return;
    this.loadingAll = true;
    this.sentinel?.classList.add('is-loading');
    let guard = 0;
    while (this.hasMore && guard < 200) {
      guard += 1;
      const ok = await this.#fetchPage();
      if (!ok) break;
    }
    this.loadingAll = false;
    this.sentinel?.classList.remove('is-loading');
  }

  // Fetch the next page and append its cards. Returns false when the fetch
  // failed, so callers can stop instead of spinning on a dead URL.
  async #fetchPage() {
    const url = this.sentinel?.dataset.nextUrl;
    if (!url) return false;
    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const nextGrid = doc.querySelector('[data-infinite-grid]');
      const nextSentinel = doc.querySelector('[data-infinite-sentinel]');
      if (nextGrid) {
        this.grid.append(...nextGrid.children);
        observeReveals(this.grid);
      }
      if (nextSentinel?.dataset.nextUrl) {
        this.sentinel.dataset.nextUrl = nextSentinel.dataset.nextUrl;
      } else {
        this.observer.disconnect();
        this.sentinel.remove();
        this.sentinel = null;
      }
      this.dispatchEvent(new CustomEvent('collection:appended', { bubbles: true }));
      return true;
    } catch (error) {
      if (error.name === 'AbortError') return false;
      // Leave the sentinel in place so scrolling retries after a hiccup.
      return false;
    }
  }

  async #loadNext() {
    if (this.loading || this.loadingAll || !this.hasMore) return;
    this.loading = true;
    this.sentinel.classList.add('is-loading');
    const ok = await this.#fetchPage();
    this.loading = false;
    this.sentinel?.classList.remove('is-loading');
    // Sparse pages (sold-out items get skipped server-side) may leave the
    // sentinel inside the viewport margin, where the observer won't fire
    // again on its own — keep pulling until it's out of reach or exhausted.
    if (ok && this.sentinel) {
      const rect = this.sentinel.getBoundingClientRect();
      if (rect.top < window.innerHeight + 900) this.#loadNext();
    }
  }
}
customElements.define('infinite-collection', InfiniteCollection);

/* ---------- <collection-view> — clean collection URL, filtered grid ----------
   Ordinary collection URLs stay canonical and visible in the address bar.
   Shopify's availability filter is requested only through the Section
   Rendering API, then its cards replace the server fallback in place. */

class CollectionView extends HTMLElement {
  #controller = null;

  connectedCallback() {
    if (!this.hasAttribute('data-available-only')) return;
    this.#loadAvailableProducts();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #loadAvailableProducts() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get('filter.v.availability') === '1') {
      this.#cleanVisibleUrl(currentUrl);
      return;
    }

    const sourceUrl = new URL(currentUrl);
    sourceUrl.searchParams.set('filter.v.availability', '1');
    sourceUrl.searchParams.delete('page');
    sourceUrl.searchParams.set('section_id', sectionId);

    const currentListing = this.querySelector('infinite-collection');
    if (!currentListing || typeof currentListing.replacePage !== 'function') return;

    this.#controller?.abort();
    this.#controller = new AbortController();
    currentListing.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(sourceUrl, {
        credentials: 'same-origin',
        signal: this.#controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const source = parsed.querySelector('collection-view[data-available-only]');
      if (!source || !currentListing.replacePage(source)) return;

      this.#updateProductCount(source);
      this.#cleanVisibleUrl(currentUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // The server-rendered collection and its pagination remain usable.
    } finally {
      currentListing.removeAttribute('aria-busy');
    }
  }

  #updateProductCount(source) {
    const currentCount = this.querySelector('[data-collection-count]');
    const sourceCount = source.querySelector('[data-collection-count]');
    if (!currentCount || !sourceCount) return;

    const text = sourceCount.textContent.trim();
    currentCount.textContent = text;
    currentCount.dataset.countOriginal = text;
  }

  #cleanVisibleUrl(url) {
    url.searchParams.delete('filter.v.availability');
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    window.history.replaceState(window.history.state, '', url);
  }
}

customElements.define('collection-view', CollectionView);

/* ---------- <free-shipping-bar> ---------- */

class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.thresholdCents || 0);
    this.message = this.querySelector('[data-shipping-message]');
    this.fill = this.querySelector('[data-shipping-fill]');
    if (!this.threshold) {
      this.hidden = true;
      return;
    }
    this.update(Number(this.dataset.totalCents || 0));
    document.addEventListener('cart:updated', (event) => {
      this.update(event.detail.cart.total_price);
    });
  }

  update(totalCents) {
    const remaining = this.threshold - totalCents;
    const progress = Math.min(100, (totalCents / this.threshold) * 100);
    if (this.fill) this.fill.style.width = `${progress}%`;
    if (!this.message) return;
    if (totalCents === 0) {
      this.message.textContent = (strings.shippingStart || 'Free shipping on orders over {{ amount }}').replace('{{ amount }}', formatMoney(this.threshold));
    } else if (remaining > 0) {
      this.message.textContent = (strings.shippingRemaining || '{{ amount }} away from free shipping').replace('{{ amount }}', formatMoney(remaining));
    } else {
      this.message.textContent = strings.shippingUnlocked || 'You have unlocked free shipping!';
    }
  }
}
customElements.define('free-shipping-bar', FreeShippingBar);

/* ---------- <carousel-slider> ---------- */

class CarouselSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    if (!this.track) return;

    this.prevButton?.addEventListener('click', () => this.#scrollByItems(-1));
    this.nextButton?.addEventListener('click', () => this.#scrollByItems(1));
    this.track.addEventListener('scroll', () => this.#updateButtons(), { passive: true });
    window.addEventListener('resize', () => this.#updateButtons(), { passive: true });
    this.#updateButtons();

    // A row that scrolls has to look like one. `data-peek` opts a carousel
    // into a single nudge of the track the first time it comes into view on
    // a touch device — the clearest way to say "there is more this way"
    // without printing an instruction.
    if (this.hasAttribute('data-peek')) this.#armPeek();
  }

  #armPeek() {
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const stillMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!coarse || stillMotion) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          obs.disconnect();
          // Only nudge if there is genuinely somewhere to go.
          if (this.track.scrollWidth <= this.track.clientWidth + 2) return;
          setTimeout(() => this.track.classList.add('is-peeking'), 500);
        }
      },
      { threshold: 0.55 }
    );
    observer.observe(this);
  }

  #scrollByItems(direction) {
    const item = this.track.querySelector(':scope > *');
    if (!item) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: 'smooth' });
  }

  #updateButtons() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 2;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= maxScroll;

    // State lives on the element so CSS can show edge fades only on the side
    // there is actually more content, and drop every affordance entirely when
    // the row already fits.
    this.classList.toggle('is-scrollable', hasOverflow);
    this.classList.toggle('is-at-start', atStart);
    this.classList.toggle('is-at-end', atEnd);

    if (!this.prevButton || !this.nextButton) return;
    this.prevButton.disabled = atStart;
    this.nextButton.disabled = atEnd;
    this.prevButton.hidden = !hasOverflow;
    this.nextButton.hidden = !hasOverflow;
  }
}
customElements.define('carousel-slider', CarouselSlider);

/* ---------- <header-menu> — mobile drawer + focus handling ---------- */

class HeaderMenu extends HTMLElement {
  #trigger = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.openButton = this.querySelector('[data-menu-open]') || document.querySelector('[data-menu-open]');
    this.openButton?.addEventListener('click', () => {
      this.#trigger = this.openButton;
      this.dialog?.showModal();
      this.openButton.setAttribute('aria-expanded', 'true');
    });
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-menu-close]')) {
        this.dialog.close();
      }
    });
    this.dialog?.addEventListener('close', () => {
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.#trigger?.focus?.();
    });
  }
}
customElements.define('header-menu', HeaderMenu);

/* ---------- <predictive-search> ---------- */

class PredictiveSearch extends HTMLElement {
  #controller = null;
  #debounce = null;

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    if (!this.input || !this.results) return;

    this.input.addEventListener('input', () => {
      clearTimeout(this.#debounce);
      this.#debounce = setTimeout(() => this.#search(), 250);
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #search() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.results.hidden = true;
      this.results.innerHTML = '';
      return;
    }
    this.#controller?.abort();
    this.#controller = new AbortController();
    try {
      const base = routes.predictiveSearch.endsWith('.json')
        ? routes.predictiveSearch
        : `${routes.predictiveSearch}.json`;
      const url = new URL(base, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '5');
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) return;
      const data = await response.json();
      const products = data.resources?.results?.products || [];
      this.#render(products, query);
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }

  #render(products, query) {
    if (!products.length) {
      this.results.innerHTML = `<p class="predictive-search__empty">${strings.noResults || 'No results found'}</p>`;
      this.results.hidden = false;
      return;
    }
    const items = products
      .map(
        (product) => `
        <li>
          <a href="${product.url}" class="predictive-search__item">
            ${product.image ? `<img src="${product.image}" alt="${escapeHtml(product.title)}" width="60" height="60" loading="lazy">` : ''}
            <span class="predictive-search__item-title">${escapeHtml(product.title)}</span>
            <span class="predictive-search__item-price">${product.price ? formatMoney(Math.round(parseFloat(product.price) * 100)) : ''}</span>
          </a>
        </li>`
      )
      .join('');
    const encodedQuery = encodeURIComponent(query);
    this.results.innerHTML = `
      <ul role="list">${items}</ul>
      <a class="predictive-search__all link-arrow" href="/search?q=${encodedQuery}">${strings.viewAll || 'View all results'}</a>`;
    this.results.hidden = false;
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------- <search-page> — ajax live search on /search results page ---------- */

class SearchPage extends HTMLElement {
  #controller = null;
  #debounce = null;

  connectedCallback() {
    this.form = this.querySelector('[data-search-form]');
    this.input = this.querySelector('[data-search-input]');
    this.heading = this.querySelector('[data-search-page-heading]');
    this.results = this.querySelector('[data-search-page-results]');
    this.sectionId = this.dataset.sectionId;
    if (!this.form || !this.input || !this.heading || !this.results || !this.sectionId) return;

    this.input.addEventListener('input', () => {
      clearTimeout(this.#debounce);
      this.#debounce = setTimeout(() => this.#search(), 350);
    });

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      clearTimeout(this.#debounce);
      this.#search();
    });

    window.addEventListener('popstate', () => {
      this.input.value = new URL(window.location).searchParams.get('q') || '';
      this.#search({ pushHistory: false });
    });

    this.results.addEventListener('click', (event) => {
      const link = event.target.closest('.pagination a');
      if (!link) return;
      event.preventDefault();
      const page = new URL(link.href, window.location.origin).searchParams.get('page');
      this.#search({ page });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #search({ pushHistory = true, page = null } = {}) {
    const query = this.input.value.trim();
    const url = new URL(this.form.action, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    if (page) url.searchParams.set('page', page);

    if (pushHistory) {
      window.history.replaceState({}, '', query ? url : window.location.pathname);
    }

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    this.classList.add('is-loading');
    try {
      const response = await fetch(fetchUrl, { signal: controller.signal });
      if (!response.ok) return;
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newHeading = doc.querySelector('[data-search-page-heading]');
      const newResults = doc.querySelector('[data-search-page-results]');
      if (newHeading) this.heading.innerHTML = newHeading.innerHTML;
      if (newResults) {
        this.results.innerHTML = newResults.innerHTML;
        observeReveals(this.results);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('search-page', SearchPage);

/* ---------- <wishlist-button> — heart toggle, product page + card ---------- */

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.productHandle;
    if (!this.handle) return;
    this.handleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#toggle();
    };
    this.handleKeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.#toggle();
    };
    this.handleWishlistUpdate = () => this.#sync();
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.#sync();
    document.addEventListener('wishlist:updated', this.handleWishlistUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('wishlist:updated', this.handleWishlistUpdate);
  }

  #sync() {
    const saved = readList(WISHLIST_KEY).includes(this.handle);
    this.classList.toggle('is-active', saved);
    this.setAttribute('aria-pressed', String(saved));
    this.setAttribute('aria-label', saved ? (strings.removeFromWishlist || 'Remove from favorites') : (strings.addToWishlist || 'Add to favorites'));
  }

  #toggle() {
    const list = readList(WISHLIST_KEY);
    const index = list.indexOf(this.handle);
    if (index === -1) {
      list.push(this.handle);
    } else {
      list.splice(index, 1);
    }
    writeList(WISHLIST_KEY, list);
    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { handle: this.handle, saved: index === -1 },
    }));
  }
}
customElements.define('wishlist-button', WishlistButton);

/* ---------- <wishlist-grid> — renders saved products on the wishlist page ---------- */

class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.loading = this.querySelector('[data-wishlist-loading]');
    this.status = this.querySelector('[data-wishlist-status]');
    this.count = this.querySelector('[data-wishlist-count]');
    this.cardCache = new Map();
    this.renderId = 0;
    this.handleUpdate = () => this.#render();
    this.#render();
    document.addEventListener('wishlist:updated', this.handleUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this.handleUpdate);
  }

  async #render() {
    const currentRender = ++this.renderId;
    const handles = [...new Set(readList(WISHLIST_KEY).filter(Boolean))];
    if (!this.grid) return;
    this.setAttribute('aria-busy', 'true');
    if (this.loading) this.loading.hidden = this.cardCache.size > 0;

    if (!handles.length) {
      this.grid.innerHTML = '';
      if (this.empty) this.empty.hidden = false;
      if (this.loading) this.loading.hidden = true;
      if (this.status) this.status.hidden = true;
      this.setAttribute('aria-busy', 'false');
      return;
    }

    if (this.empty) this.empty.hidden = true;
    if (this.status) this.status.hidden = false;
    this.#updateCount(handles.length);

    const cards = await Promise.all(handles.map(async (handle) => ({
      handle,
      html: await this.#fetchCard(handle),
    })));
    if (currentRender !== this.renderId) return;

    const availableCards = cards.filter((card) => card.html);
    this.grid.innerHTML = availableCards.map((card, index) => (
      `<li class="wishlist-page__item reveal" style="--wishlist-index:${index}">${card.html}</li>`
    )).join('');
    observeReveals(this.grid);
    this.#updateCount(availableCards.length);

    if (!availableCards.length && this.empty) this.empty.hidden = false;
    if (this.status) this.status.hidden = availableCards.length === 0;
    if (this.loading) this.loading.hidden = true;
    this.setAttribute('aria-busy', 'false');
  }

  #updateCount(value) {
    if (!this.count) return;
    const template = value === 1 ? this.dataset.countOne : this.dataset.countOther;
    this.count.textContent = (template || `${value} saved pieces`).replace(/\{\{\s*count\s*\}\}/, value);
  }

  async #fetchCard(handle) {
    if (this.cardCache.has(handle)) return this.cardCache.get(handle);

    try {
      const response = await fetch(`/products/${handle}?view=card`);
      if (!response.ok) return '';
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const card = doc.querySelector('.product-card');
      const cardHtml = card ? card.outerHTML : '';
      if (cardHtml) this.cardCache.set(handle, cardHtml);
      return cardHtml;
    } catch {
      return '';
    }
  }
}
customElements.define('wishlist-grid', WishlistGrid);

/* ---------- <recently-viewed-row> — minimal strip at the bottom of product pages ---------- */

class RecentlyViewedRow extends HTMLElement {
  async connectedCallback() {
    const currentHandle = this.dataset.currentHandle;
    const handles = readList(RECENTLY_VIEWED_KEY)
      .filter((handle) => handle !== currentHandle)
      .slice(0, Number(this.dataset.limit || 4));
    if (!handles.length) {
      this.hidden = true;
      return;
    }
    const items = await Promise.all(handles.map((handle) => this.#fetchProduct(handle)));
    const track = this.querySelector('[data-recently-viewed-track]');
    if (!track) return;
    const html = items
      .filter(Boolean)
      .map((product) => {
        let thumb = '';
        if (product.featured_image) {
          const sep = product.featured_image.includes('?') ? '&' : '?';
          const src = (w) => `${product.featured_image}${sep}width=${w}`;
          thumb = `<img src="${src(240)}" srcset="${src(120)} 120w, ${src(180)} 180w, ${src(240)} 240w, ${src(360)} 360w" sizes="120px" alt="${escapeHtml(product.title)}" loading="lazy" width="120" height="150">`;
        }
        return `
        <li class="recently-viewed__item">
          <a href="${product.url}" class="recently-viewed__link">
            <span class="recently-viewed__media">${thumb}</span>
            <span class="recently-viewed__title">${escapeHtml(product.title)}</span>
            <span class="recently-viewed__price">${formatMoney(product.price)}</span>
          </a>
        </li>`;
      })
      .join('');
    track.innerHTML = html;
    this.hidden = !html;
  }

  async #fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }
}
customElements.define('recently-viewed-row', RecentlyViewedRow);

/* ---------- <gift-note> — checkbox-revealed cart attribute, drawer + cart page ---------- */

class GiftNote extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('[data-gift-note-checkbox]');
    this.field = this.querySelector('[data-gift-note-field]');
    this.textarea = this.querySelector('[data-gift-note-textarea]');
    if (!this.checkbox || !this.field || !this.textarea) return;

    this.checkbox.addEventListener('change', () => {
      this.field.hidden = !this.checkbox.checked;
      if (this.checkbox.checked) {
        this.textarea.focus();
        if (this.textarea.value) this.#save(this.textarea.value);
      } else {
        this.textarea.value = '';
        this.#save('');
      }
    });

    let debounce;
    this.textarea.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => this.#save(this.textarea.value), 500);
    });
  }

  async #save(value) {
    try {
      await fetch(routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { 'Gift Note': value } }),
      });
    } catch {
      /* best-effort — a failed save just means the note isn't attached */
    }
  }
}
customElements.define('gift-note', GiftNote);

/* ---------- <product-badge-info> — tag-driven product badge details ---------- */

class ProductBadgeInfo extends HTMLElement {
  connectedCallback() {
    if (this.button) return;
    this.button = this.querySelector('[data-badge-info-trigger]');
    this.panel = this.querySelector('[data-badge-info-panel]');
    if (!this.button || !this.panel) return;

    this.handleButtonClick = (event) => {
      event.stopPropagation();
      if (this.panel.hidden) this.open();
      else this.close();
    };
    this.handleDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    this.handleKeydown = (event) => {
      if (event.key !== 'Escape' || this.panel.hidden) return;
      event.preventDefault();
      this.close();
      this.button.focus();
    };
    this.handleViewportChange = () => this.close();

    this.button.addEventListener('click', this.handleButtonClick);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.handleViewportChange, { passive: true });
    window.addEventListener('scroll', this.handleViewportChange, { passive: true, capture: true });
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleButtonClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.handleViewportChange);
    window.removeEventListener('scroll', this.handleViewportChange, true);
    this.button = null;
    this.panel = null;
  }

  open() {
    for (const badge of document.querySelectorAll('product-badge-info.is-open')) {
      if (badge !== this) badge.close();
    }
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    this.#positionPanel();
  }

  close() {
    if (!this.panel || this.panel.hidden) return;
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
  }

  #positionPanel() {
    const viewportMargin = 16;
    const gap = 10;
    const buttonRect = this.button.getBoundingClientRect();
    const panelWidth = this.panel.offsetWidth;
    const panelHeight = this.panel.offsetHeight;
    const centeredLeft = buttonRect.left + buttonRect.width / 2 - panelWidth / 2;
    const maxLeft = Math.max(viewportMargin, window.innerWidth - panelWidth - viewportMargin);
    const left = Math.min(maxLeft, Math.max(viewportMargin, centeredLeft));
    const belowTop = buttonRect.bottom + gap;
    const aboveTop = buttonRect.top - panelHeight - gap;
    const opensAbove = belowTop + panelHeight > window.innerHeight - viewportMargin && aboveTop >= viewportMargin;
    const top = opensAbove ? aboveTop : belowTop;
    const pointer = Math.min(panelWidth - 18, Math.max(18, buttonRect.left + buttonRect.width / 2 - left));

    this.panel.style.left = `${Math.round(left)}px`;
    this.panel.style.top = `${Math.round(top)}px`;
    this.panel.style.setProperty('--product-badge-pointer', `${Math.round(pointer)}px`);
    this.panel.classList.toggle('is-above', opensAbove);
  }
}
customElements.define('product-badge-info', ProductBadgeInfo);

/* ---------- <product-lightbox> — click-to-zoom overlay for the gallery ---------- */

// Two stops: fit → 2×. One click zooms in on whatever point was
// clicked, a second click resets back to fit — plenty for these
// product photos without needing a multi-level zoom ladder.
const LIGHTBOX_ZOOM_LEVELS = [1, 2];

class ProductLightbox extends HTMLElement {
  connectedCallback() {
    // Reparenting into <body> below moves this element in the DOM,
    // which re-triggers connectedCallback (disconnect + reconnect) —
    // without this guard every listener below would be attached twice.
    if (this.dataset.lightboxInit) return;
    this.dataset.lightboxInit = 'true';

    this.gallery = document.querySelector('.product__gallery');
    this.stage = this.querySelector('[data-lightbox-stage]');
    if (!this.gallery || !this.stage) return;

    // Moved to a direct child of <body> so no ancestor (a sticky gallery
    // column, a reveal-animation wrapper, anything with its own z-index)
    // can trap this in a losing stacking context — position: fixed +
    // z-index only wins against the WHOLE page when nothing wraps it.
    document.body.appendChild(this);

    this.slides = Array.from(this.gallery.querySelectorAll('.product__media'));
    this.index = 0;
    this.zoomIndex = 0;

    this.slides.forEach((slide, i) => {
      slide.classList.add('is-zoomable');
      slide.addEventListener('click', () => this.open(i));
    });

    this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.show(this.index - 1);
    });
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.show(this.index + 1);
    });
    this.querySelector('[data-lightbox-zoom-in]')?.addEventListener('click', () => this.#stepZoom(1));
    this.querySelector('[data-lightbox-zoom-out]')?.addEventListener('click', () => this.#stepZoom(-1));

    this.swipeStart = null;
    this.suppressStageClick = false;
    this.swipeClickTimer = 0;
    this.stage.addEventListener('pointerdown', (event) => this.#startSwipe(event));
    this.stage.addEventListener('pointerup', (event) => this.#finishSwipe(event));
    this.stage.addEventListener('pointercancel', (event) => this.#cancelSwipe(event.pointerId));
    this.stage.addEventListener('dragstart', (event) => event.preventDefault());
    this.stage.addEventListener('click', (event) => {
      if (this.suppressStageClick) {
        event.preventDefault();
        this.suppressStageClick = false;
        clearTimeout(this.swipeClickTimer);
        return;
      }
      if (event.target === this.stage) return; // clicked empty stage padding, not the media
      const max = LIGHTBOX_ZOOM_LEVELS.length - 1;
      const next = this.zoomIndex >= max ? 0 : this.zoomIndex + 1;
      this.#zoomTo(next, event.clientX, event.clientY);
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (this.hidden) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      if (event.key === 'ArrowRight') this.show(this.index + 1);
      if (event.key === '+' || event.key === '=') this.#stepZoom(1);
      if (event.key === '-') this.#stepZoom(-1);
    });

    // Rotating a phone or resizing the window mid-zoom leaves the
    // scroll math computed for a viewport that no longer exists —
    // simplest correct fix is to drop back to "fit" rather than let
    // the zoomed image sit at a stale, now-meaningless scroll offset.
    let resizeTimer;
    window.addEventListener('resize', () => {
      if (this.hidden || this.zoomIndex === 0) return;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.show(this.index), 150);
    });
  }

  open(index) {
    this.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    this.show(index);
  }

  close() {
    this.hidden = true;
    document.documentElement.style.overflow = '';
    this.#cancelSwipe(this.swipeStart?.pointerId);
    this.suppressStageClick = false;
    clearTimeout(this.swipeClickTimer);
    this.stage.innerHTML = '';
  }

  show(index) {
    const total = this.slides.length;
    this.index = (index + total) % total;
    const source = this.slides[this.index].firstElementChild;
    if (!source) return;
    const clone = source.cloneNode(true);
    clone.removeAttribute('loading');
    this.stage.innerHTML = '';
    this.stage.appendChild(clone);
    this.zoomIndex = 0;
    this.#applyZoom();
    if (clone.tagName === 'VIDEO') {
      clone.muted = true;
      clone.play?.().catch(() => {});
    }
  }

  #startSwipe(event) {
    if (this.zoomIndex !== 0 || event.button > 0) return;
    this.swipeStart = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    try {
      this.stage.setPointerCapture(event.pointerId);
    } catch {
      /* Pointer capture is best-effort on older mobile browsers. */
    }
  }

  #finishSwipe(event) {
    const start = this.swipeStart;
    this.#cancelSwipe(event.pointerId);
    if (!start || start.pointerId !== event.pointerId || this.zoomIndex !== 0) return;

    const distanceX = event.clientX - start.x;
    const distanceY = event.clientY - start.y;
    const threshold = Math.max(44, Math.min(72, this.stage.clientWidth * 0.14));
    if (Math.abs(distanceX) < threshold || Math.abs(distanceX) <= Math.abs(distanceY) * 1.2) return;

    event.preventDefault();
    this.suppressStageClick = true;
    clearTimeout(this.swipeClickTimer);
    this.swipeClickTimer = window.setTimeout(() => {
      this.suppressStageClick = false;
    }, 400);
    this.show(this.index + (distanceX < 0 ? 1 : -1));
  }

  #cancelSwipe(pointerId) {
    if (pointerId !== undefined && this.stage.hasPointerCapture?.(pointerId)) {
      this.stage.releasePointerCapture(pointerId);
    }
    this.swipeStart = null;
  }

  /** Button/keyboard zoom: steps one level at a time, anchored on the stage's current visible center. */
  #stepZoom(direction) {
    const max = LIGHTBOX_ZOOM_LEVELS.length - 1;
    const next = Math.min(max, Math.max(0, this.zoomIndex + direction));
    if (next === this.zoomIndex) return;
    const rect = this.stage.getBoundingClientRect();
    this.#zoomTo(next, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  /** Click-to-zoom: re-centers on whatever point of the image was under the cursor, at every step. */
  #zoomTo(zoomIndex, clientX, clientY) {
    const media = this.stage.firstElementChild;
    if (!media) return;
    const mediaRect = media.getBoundingClientRect();
    const fracX = mediaRect.width > 0 ? (clientX - mediaRect.left) / mediaRect.width : 0.5;
    const fracY = mediaRect.height > 0 ? (clientY - mediaRect.top) / mediaRect.height : 0.5;

    this.zoomIndex = zoomIndex;
    this.#applyZoom();

    if (this.zoomIndex === 0) {
      this.stage.scrollLeft = 0;
      this.stage.scrollTop = 0;
      return;
    }

    requestAnimationFrame(() => {
      const stageRect = this.stage.getBoundingClientRect();
      const targetX = fracX * this.stage.scrollWidth;
      const targetY = fracY * this.stage.scrollHeight;
      this.stage.scrollLeft = targetX - (clientX - stageRect.left);
      this.stage.scrollTop = targetY - (clientY - stageRect.top);
    });
  }

  #applyZoom() {
    const max = LIGHTBOX_ZOOM_LEVELS.length - 1;
    const zoom = LIGHTBOX_ZOOM_LEVELS[this.zoomIndex];
    const media = this.stage.firstElementChild;
    if (media) {
      media.style.width = zoom > 1 ? `${zoom * 100}%` : '';
      media.style.cursor = this.zoomIndex >= max ? 'zoom-out' : 'zoom-in';
    }
    this.stage.classList.toggle('is-zoomed', zoom > 1);
    const zoomIn = this.querySelector('[data-lightbox-zoom-in]');
    const zoomOut = this.querySelector('[data-lightbox-zoom-out]');
    if (zoomIn) zoomIn.disabled = this.zoomIndex >= max;
    if (zoomOut) zoomOut.disabled = this.zoomIndex <= 0;
  }
}
customElements.define('product-lightbox', ProductLightbox);
